import React, { useEffect, useLayoutEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Clock } from "lucide-react";
import Avatar from "../../components/common/ngwAvatar";
import { setTopNavNameTitle } from "../../redux/features/mobileLayout/slice";
import { useAppDispatch } from "../../redux/store";
import { OpenShiftDetail } from "../../types/model/openShift";
import { getOpenShiftDetails } from "../../redux/features/openShift";
import { GetOpenShiftDetailsRequest } from "../../types/request";
import { RootState } from "../../redux/store";
import DateTimeUtil from "../../utils/dateTime";

// Test flag - set to true to use static data
const IS_TEST = false;

const OpenShiftDetails: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const { selectedOpenShiftDetail, isLoadingOpenShiftDetails, error } = useSelector(
        (state: RootState) => state.openShift
    );
    const [shiftDetails, setShiftDetails] = useState<OpenShiftDetail | null>(null);

    // Static test data
    const testShiftDetails: OpenShiftDetail = {
        os_sch_open_shift_transactions_id: "68",
        shift_date: "2025-08-15",
        status: "picked",
        wf_txn_header_id: "22",
        source_type: "manager_created",
        shift_name: "Afternoon 5PM",
        shift_code: "128",
        shift_start_time: "17:00:00",
        shift_end_time: "01:00:00",
        claim_requestor: {
            employee_id: "53642",
            emp_name: "Eduard Eduard",
            image_url: "https://ngw-app.s3.us-east-1.amazonaws.com/no_image_employee.png",
            role: "Employee",
        },
        release_requestor: {
            employee_id: "53642",
            emp_name: "Eduard Eduard",
            image_url: "https://ngw-app.s3.us-east-1.amazonaws.com/no_image_employee.png",
            role: "Employee",
        },
        created_details: {
            role: "Manager",
        },
        workflow_current_step_details: {
            step_code: "1",
            current_status: "0",
            status_text: "Pending",
        },
        workflow_details: [
            {
                step_code: "1",
                current_status: "0",
                actor_json: {
                    "53646": {
                        employee_id: "53646",
                        name: "Daniel Daniel",
                        image_url: "https://ngw-app.s3.us-east-1.amazonaws.com/no_image_employee.png",
                        openshift_details: {
                            os_sch_open_shift_transactions_id: "68",
                            shift_date: "2025-08-15",
                            status: "picked",
                            shift_name: "Afternoon 5PM",
                            shift_code: "128",
                            shift_start_time: "17:00:00",
                            shift_end_time: "01:00:00",
                        },
                    },
                },
                wf_task_acted_json: null,
                wf_event_json: {
                    REF_ID: 68,
                    WF_CODE: "OPEN_SHIFT_PICK",
                    SWAPPING_DETAILS: {
                        Approved: [],
                        Rejected: [],
                    },
                },
            },
            {
                step_code: "L1_APPROVAL",
                current_status: "pending",
                actor_json: {
                    "53647": {
                        employee_id: "emp-004",
                        name: "Sam Miller",
                        image_url:
                            "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
                        openshift_details: {
                            os_sch_open_shift_transactions_id: "test-123",
                            shift_date: "2024-08-18",
                            status: "pending",
                            shift_name: "City Library Shift",
                            shift_code: "CL001",
                            shift_start_time: "09:00:00",
                            shift_end_time: "17:00:00",
                        },
                    },
                },
                wf_task_acted_json: {
                    RESULT: {
                        APPROVED: [],
                        REJECTED: [],
                    },
                    APPROVED: "N",
                    APPROVED_BY: {
                        employee_id: "emp-004",
                        name: "Sam Miller",
                        image_url:
                            "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
                        openshift_details: {
                            os_sch_open_shift_transactions_id: "test-123",
                            shift_date: "2024-08-18",
                            status: "pending",
                            shift_name: "City Library Shift",
                            shift_code: "CL001",
                            shift_start_time: "09:00:00",
                            shift_end_time: "17:00:00",
                        },
                    },
                    APPROVED_ON: "2024-08-18T10:00:00",
                    APPROVAL_NOTE: "Pending revig add ew",
                },
                wf_event_json: {
                    REF_ID: 12346,
                    WF_CODE: "OS_APPROVAL",
                    SWAPPING_DETAILS: {
                        Approved: [],
                        Rejected: [],
                    },
                },
            },
        ],
    };

    useLayoutEffect(() => {
        dispatch(setTopNavNameTitle("Open Shift Details"));
    }, []);

    useEffect(() => {
        if (IS_TEST) {
            // Use static test data
            setShiftDetails(testShiftDetails);
        } else if (id) {
            // Use real API data
            const request: GetOpenShiftDetailsRequest = { shift_id: id };
            dispatch(getOpenShiftDetails(request));
        }
    }, [dispatch, id]);

    useEffect(() => {
        if (!IS_TEST && selectedOpenShiftDetail) {
            setShiftDetails(selectedOpenShiftDetail);
        }
    }, [selectedOpenShiftDetail]);

    const getStatusColor = (status: string) => {
        switch (status) {
            case "1":
            case "approved":
                return "bg-green-500 text-white";
            case "0":
            case "pending":
                return "bg-yellow-500 text-white";
            case "rejected":
                return "bg-red-500 text-white";
            default:
                return "bg-gray-500 text-white";
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case "1":
            case "approved":
                return "Approved";
            case "0":
            case "pending":
                return "Pending";
            case "rejected":
                return "Rejected";
            default:
                return status || "Unknown";
        }
    };

    if (!IS_TEST && isLoadingOpenShiftDetails) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!IS_TEST && (error || !shiftDetails)) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen p-4">
                <div className="text-red-500 text-lg mb-4">Failed to load shift details</div>
                <button onClick={() => navigate(-1)} className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                    Go Back
                </button>
            </div>
        );
    }

    // Use test data if in test mode and no shift details loaded yet
    const displayData = shiftDetails || testShiftDetails;

    // Helper function to get employee name safely
    const getEmployeeName = (employee: any) => {
        if (employee?.emp_name) return employee.emp_name;
        if (employee?.name) return employee.name;
        return "Unknown";
    };

    // Helper function to get employee image safely
    const getEmployeeImage = (employee: any) => {
        if (
            employee?.image_url &&
            employee.image_url !== "https://ngw-app.s3.us-east-1.amazonaws.com/no_image_employee.png"
        ) {
            return employee.image_url;
        }
        return null;
    };

    // Build a date-time string (YYYY-MM-DD HH:mm:ss) if parts exist
    const buildDateTime = (date?: string, time?: string) => {
        if (!date || !time) return undefined;
        return `${date} ${time}`;
    };

    const shiftStartDateTime = buildDateTime(displayData.shift_date, displayData.shift_start_time);
    const shiftEndDateTime = buildDateTime(displayData.shift_date, displayData.shift_end_time);

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="w-full md:w-1/2 mx-auto">
                {/* Header */}
                <div className="bg-white shadow-sm border-b">
                    <div className="flex items-center justify-between p-4">
                        <div className="flex items-center">
                            {displayData?.shift_name && (
                                <h1 className="text-lg font-semibold text-gray-900">
                                    {displayData.shift_name || "Open Shift"}
                                </h1>
                            )}
                            {displayData?.shift_date &&
                                displayData?.shift_start_time &&
                                displayData?.shift_end_time && (
                                    <div className="flex items-center text-sm text-gray-600 ml-8 pl-6 border-l border-gray-200">
                                        <Clock className="h-4 w-4 mr-1" />
                                        <span>
                                            {DateTimeUtil.formatDateTimeRangeUtc(
                                                `${displayData?.shift_date} ${displayData?.shift_start_time}`,
                                                `${displayData?.shift_date} ${displayData?.shift_end_time}`
                                            )}
                                        </span>
                                    </div>
                                )}
                        </div>
                    </div>
                </div>

                <div className="pt-6 space-y-6">
                    {/* Shift Overview */}
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <h2 className="text-lg font-semibold text-gray-900 mb-3">Shift Overview</h2>
                        <div className="flex items-start justify-between">
                            <div className="flex items-start">
                                <Avatar
                                    name={displayData.created_details?.role || "Manager"}
                                    className="w-12 h-12 mr-4"
                                />
                                <div className="space-y-1">
                                    <div className="text-sm text-gray-600">
                                        Posted by {displayData.created_details?.role || "Manager"}
                                    </div>
                                    {displayData.created_details?.role && (
                                        <div className="text-xs inline-block px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 uppercase tracking-wide">
                                            {displayData.created_details.role}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {displayData.shift_date && (
                                <div className="text-sm text-gray-500 text-right">{displayData.shift_date}</div>
                            )}
                        </div>

                        {/* Compact details */}
                        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <div className="flex items-center gap-3 flex-wrap">
                                    {displayData.shift_name && (
                                        <span className="text-base font-semibold text-gray-900">
                                            {displayData.shift_name}
                                        </span>
                                    )}
                                    {displayData.shift_code && (
                                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-medium tracking-wide">
                                            #{displayData.shift_code}
                                        </span>
                                    )}
                                    {displayData.status && (
                                        <span
                                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusColor(
                                                displayData.status
                                            )}`}
                                        >
                                            {getStatusText(displayData.status)}
                                        </span>
                                    )}
                                </div>
                                {(displayData.shift_start_time || displayData.shift_end_time) && (
                                    <div className="mt-1 text-sm text-gray-600">
                                        {displayData.shift_start_time && displayData.shift_end_time
                                            ? DateTimeUtil.formatDateTimeRangeUtc(
                                                  `${displayData.shift_date} ${displayData.shift_start_time}`,
                                                  `${displayData.shift_date} ${displayData.shift_end_time}`
                                              )
                                            : `${DateTimeUtil.formatElegantTime(
                                                  `${displayData.shift_date} ${displayData.shift_start_time}`
                                              )} – ${DateTimeUtil.formatElegantTime(
                                                  `${displayData.shift_date} ${displayData.shift_end_time}`
                                              )}`}
                                    </div>
                                )}
                            </div>
                            {displayData.shift_date && (
                                <div className="text-sm text-gray-500 sm:text-right">{displayData.shift_date}</div>
                            )}
                        </div>
                    </div>

                    {/* Requested by */}
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <h2 className="text-lg font-semibold text-gray-900 mb-3">Requested by</h2>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <Avatar
                                    src={getEmployeeImage(displayData.claim_requestor) || undefined}
                                    name={getEmployeeName(displayData.claim_requestor)}
                                    className="w-10 h-10 mr-3"
                                />
                                <div className="font-semibold text-gray-900">
                                    {getEmployeeName(displayData.claim_requestor) || "Unknown"}
                                </div>
                            </div>
                            {shiftStartDateTime && shiftEndDateTime && (
                                <div className="text-sm text-gray-500 flex items-center">
                                    <Clock className="h-4 w-4 mr-1" />
                                    {DateTimeUtil.formatTimeRangeUtc(shiftStartDateTime, shiftEndDateTime)}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Approval History */}
                    {displayData.workflow_details && displayData.workflow_details.length > 0 && (
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4">Approval History</h2>
                            <div className="bg-gray-50 rounded-lg p-4 border">
                                <div className="flex flex-col space-y-0 relative">
                                    {displayData.workflow_details.map((workflow, index) => {
                                        const actorKey = Object.keys(workflow.actor_json || {})[0];
                                        const actor = actorKey ? workflow.actor_json?.[actorKey] : null;

                                        if (!actor) return null;

                                        const isLastItem = index === (displayData.workflow_details?.length || 0) - 1;
                                        const isPending = workflow.current_status === "0";
                                        const isApproved = workflow.current_status === "1";

                                        return (
                                            <div key={index} className="flex items-stretch relative min-h-[80px]">
                                                {/* Vertical line connecting steps, except for last step */}
                                                {!isLastItem && (
                                                    <div className="absolute left-6 top-12 w-px h-[calc(100%_-_12px)] bg-gray-200 z-0"></div>
                                                )}

                                                {/* Avatar */}
                                                <div className="flex flex-col items-center mr-4 z-10">
                                                    <Avatar
                                                        src={actor.image_url}
                                                        name={getEmployeeName(actor)}
                                                        className="w-12 h-12"
                                                    />
                                                </div>

                                                {/* Actor details */}
                                                <div className="flex-1 flex flex-col justify-center">
                                                    <span className="font-semibold text-gray-900 text-base leading-tight">
                                                        {getEmployeeName(actor)}
                                                    </span>

                                                    <div className="mt-1 mb-3">
                                                        {isPending && (
                                                            <span className="inline-flex items-center text-sm">
                                                                <Clock className="w-4 h-4 mr-1 text-gray-400" />
                                                                <span className="text-gray-600">
                                                                    Waiting for approval
                                                                </span>
                                                                {shiftStartDateTime && shiftEndDateTime && (
                                                                    <span className="text-gray-500 ml-2">
                                                                        •{" "}
                                                                        {DateTimeUtil.formatTimeRangeUtc(
                                                                            shiftStartDateTime,
                                                                            shiftEndDateTime
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </span>
                                                        )}

                                                        {isApproved && (
                                                            <span className="inline-flex items-center text-sm">
                                                                <div className="w-4 h-4 mr-1 bg-green-500 rounded-full flex items-center justify-center">
                                                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                                                </div>
                                                                <span className="text-gray-800">
                                                                    Approved
                                                                    {shiftStartDateTime && shiftEndDateTime && (
                                                                        <span className="text-gray-600 ml-1">
                                                                            •{" "}
                                                                            {DateTimeUtil.formatTimeRangeUtc(
                                                                                shiftStartDateTime,
                                                                                shiftEndDateTime
                                                                            )}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OpenShiftDetails;
